import { onUnmounted, ref, watch, type Ref } from 'vue';
import { debounce } from 'lodash';
import type { Contributor } from '@ilteducation/content-ref-data-types';
import { searchContributors } from '@/data-provider/refDataProvider';

const DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 3;

/**
 * Looks up contributors by full name while `query` is being typed. `enabled` is
 * false for rows that are already linked to a contributor - those need no
 * suggestions until they are unlinked again.
 */
export const useContributorSearch = (query: Ref<string>, enabled: Ref<boolean>) => {
  const results = ref<Contributor[]>([]);
  const isSearching = ref(false);

  const run = debounce(async (searchQuery: string) => {
    isSearching.value = true;
    try {
      results.value = await searchContributors(searchQuery);
    } catch (error) {
      console.error('Contributor search failed', error);
      results.value = [];
    } finally {
      isSearching.value = false;
    }
  }, DEBOUNCE_MS);

  const reset = () => {
    run.cancel();
    results.value = [];
    isSearching.value = false;
  };

  // Immediate, so a row that arrives already filled in - from fetched book
  // metadata, or from the book being edited - looks up its contributor too.
  watch(
    [query, enabled],
    ([searchQuery, isEnabled]) => {
      if (!isEnabled || searchQuery.trim().length < MIN_QUERY_LENGTH) {
        reset();
        return;
      }
      run(searchQuery.trim());
    },
    { immediate: true },
  );

  onUnmounted(() => run.cancel());

  return { results, isSearching };
};
